import { EventCategory, ActivityResultType } from '@fluidx/office-fluid-types';
import { Activity, EnumObjects, Funnel, makeStringDataField, onNotification, SimpleTelemetryLogger, SuppressNexus, UserAction } from '@microsoft/oteljs';
import { getOtelDataField } from './OtelDataFieldHelpers';
import { isQoSEvent, prepareQoSEvent } from '../Health';
import { getAriaInfoByAudience } from './LogProcessorHelpers';
export class BaseOtelLogProcessor {
    constructor(audience, initialPersistentProps) {
        this.initialPersistentProps = initialPersistentProps;
        this.ashaLoadStarted = false;
        this.telemetryLogger = new SimpleTelemetryLogger(undefined, undefined, this.getTelemetryConfig(audience));
        // We are adding our own PartA field here, if we end up adding more, we might extract this to a method.
        this.telemetryLogger.partAFields.push(makeStringDataField('Loop.Audience', audience));
        this.ariaInfo = getAriaInfoByAudience(audience);
        this.telemetryLogger.setTenantToken(this.ariaInfo.namespacePrefix, this.ariaInfo.token, SuppressNexus);
        // Listen to error notifications sent by Otel and log them to the console when audience is non-production
        if (this.isNonProductionAudience(audience)) {
            this.registerOtelErrorsListener();
        }
    }
    /**
     * Method that logs the telemetry event based on the event category
     * @param event Telemetry event with namespace, category and a property bag of other fields
     */
    async logEvent(event) {
        let { namespace, category, props } = event;
        if (category === EventCategory.UserAction) {
            this.logUserActionEventToOtel(event);
        }
        else if (category === EventCategory.Activity) {
            this.logActivityEventToOtel(event);
        }
        else {
            if (['AshaPillarAdd', 'AshaPillarRemove', 'AshaError'].includes(props.eventName)) {
                this.loadAsha();
                this.ashaUtils
                    ?.then(({ handleAshaEvent }) => handleAshaEvent(event))
                    .catch(() => {
                    this.logDefaultEventToOtel({
                        category: EventCategory.Error,
                        namespace,
                        props: { eventName: `${props.eventName}Failure` }
                    });
                });
                return;
            }
            if (category === EventCategory.Error && isQoSEvent(props)) {
                // Prepare ASHA QoS error events
                // TODO: Task 5541083: Move ASHA QoS event preprocessing out of OtelLogProcessor
                props = prepareQoSEvent(props);
            }
            this.logDefaultEventToOtel({ namespace, category, props });
        }
    }
    shutdown() {
        // shut down ASHA instance if there is one
        this.ashaUtils
            ?.then(({ shutdownAshaForFluid }) => shutdownAshaForFluid())
            .catch(() => {
            this.logDefaultEventToOtel({
                category: EventCategory.Error,
                props: { eventName: 'AshaShutdownFailure' }
            });
        });
    }
    logDefaultEventToOtel(event) {
        this.logEventToOtelInternal(event, (telemetryEvent) => {
            this.prepareDefaultOtelEvent(telemetryEvent, event);
        });
    }
    logActivityEventToOtel(event) {
        this.logEventToOtelInternal(event, (telemetryEvent) => {
            // Sending props to make sure that fields that are needed for the activity are present
            /* eslint-disable-next-line @fluidx/ffx-rules/no-object-literal-type-assertion
      -- TODO: This cast will mask bugs in product code. Replace cast with type declaration or add rationale for why it's exceptional and needed. */
            this.prepareActivityOtelEvent(telemetryEvent, {
                ...event.props
            });
        });
    }
    logUserActionEventToOtel(event) {
        this.logEventToOtelInternal(event, (telemetryEvent) => {
            // Sending props to make sure that fields that are needed for the userAction are present
            this.prepareUserActionOtelEvent(telemetryEvent, 
            /* eslint-disable-next-line @fluidx/ffx-rules/no-object-literal-type-assertion
    -- TODO: This cast will mask bugs in product code. Replace cast with type declaration or add rationale for why it's exceptional and needed. */
            {
                ...event.props
            }, event.namespace);
        });
    }
    /**
     * Returns the telemetry config to be passed to the SimpleTelemetryLogger. By default we use Otel's ability to push events to a queue, an optimization useful when host applications want to delay load OneDs sink
     */
    getTelemetryConfig(audience) {
        const defaultTelemetryConfig = { enableQueue: true };
        if (!this.isNonProductionAudience(audience)) {
            return { ...defaultTelemetryConfig, disableValidation: true };
        }
        return defaultTelemetryConfig;
    }
    /**
     * If the audience is Automation, Local or Staging we consider it non-production for the purpose of telemetry event validation.
     */
    isNonProductionAudience(audience) {
        if (audience === 'Automation' || audience === 'Local' || audience === 'Staging') {
            return true;
        }
        return false;
    }
    /**
     * Registers a listener on Otel Notifications to log to console when the notification is an error.
     */
    registerOtelErrorsListener() {
        onNotification().addListener((event) => {
            if (event.category === 0 && event.level === 0) {
                console.error(event.message());
            }
        });
    }
    getDataFieldsFromProps(props) {
        let dataFields = [];
        Object.keys(props).forEach((key) => {
            const dataField = getOtelDataField(props[key], key);
            if (!dataField) {
                return;
            }
            dataFields.push(dataField);
        });
        return dataFields;
    }
    logEventToOtelInternal(event, prepareEvent) {
        const telemetryEvent = this.getBasicOtelEvent(event);
        prepareEvent(telemetryEvent);
        this.telemetryLogger.sendTelemetryEvent(telemetryEvent);
    }
    getBasicOtelEvent(event) {
        // Otel APIs will fail if the last part of the eventName is not uppercase
        let eventType = event.category && `${event.category.charAt(0).toUpperCase()}${event.category.substring(1)}`;
        // subCategory is appended at the end of the Table name in case of Generic events
        // If a subCategory is specified for any event other than a generic event, it will simply be logged as a column in the table
        if (event.props.subCategory && event.category === EventCategory.Generic) {
            eventType =
                eventType +
                    `.${event.props.subCategory.charAt(0).toUpperCase()}${event.props.subCategory.substring(1)}`;
        }
        return {
            eventName: `${this.ariaInfo.namespacePrefix}${event.namespace ? '.' + event.namespace : ''}.${eventType}`,
            eventFlags: {
                diagnosticLevel: EnumObjects.DiagnosticLevel.RequiredServiceData
            }
        };
    }
    prepareDefaultOtelEvent(telemetryEvent, event) {
        telemetryEvent.eventFlags.dataCategories = EnumObjects.DataCategories.ProductServicePerformance;
        telemetryEvent.dataFields = this.getDataFieldsFromProps({ ...event.props });
    }
    mapActivityResultTypeToCode(resultType) {
        switch (resultType) {
            case ActivityResultType.Failure:
                return 0;
            case ActivityResultType.Success:
                return 1;
            case ActivityResultType.Cancelled:
                return 2;
        }
    }
    prepareBaseActivityOtelEvent(telemetryEvent, props, fromUserAction) {
        if (!props.durationMs) {
            props.durationMs = 0;
        }
        // Otel's Activity contract expects duration in microSeconds (not milliseconds), and expects it to be a round value.
        const durationInMicroseconds = Math.floor(props.durationMs * 1000);
        const { durationMs, succeeded, resultType, ...propsWithoutActivityData } = props;
        telemetryEvent.eventContract = {
            name: Activity.contractName,
            dataFields: Activity.getFields({
                duration: durationInMicroseconds,
                count: 1,
                aggMode: 0,
                success: succeeded,
                result: !fromUserAction && resultType
                    ? { code: this.mapActivityResultTypeToCode(resultType), type: resultType }
                    : undefined
            })
        };
        // returning new Props to make sure that we don't lose the fields in props that are not used for the activity
        return propsWithoutActivityData;
    }
    prepareActivityOtelEvent(telemetryEvent, props) {
        const eventName = props.eventName;
        // Each activity is bucketed into its own eventName. Otel APIs will fail if the eventName doesn't start with a capital letter
        telemetryEvent.eventName += `.${eventName.charAt(0).toUpperCase()}${eventName.substring(1)}`;
        telemetryEvent.eventFlags.dataCategories = EnumObjects.DataCategories.ProductServicePerformance;
        const unusedProps = this.prepareBaseActivityOtelEvent(telemetryEvent, props, false);
        // Exception added when eslint rule was added, this should be revisited when modifying this code
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        telemetryEvent.dataFields = this.getDataFieldsFromProps(unusedProps || {});
    }
    prepareUserActionOtelEvent(telemetryEvent, props, namespace) {
        const userActionEventName = props.eventName;
        // Each user action is bucketed into its own eventName. Otel APIs will fail if the eventName doesn't start with a capital letter
        telemetryEvent.eventName += `.${userActionEventName.charAt(0).toUpperCase()}${userActionEventName.substring(1)}`;
        telemetryEvent.eventFlags.dataCategories = EnumObjects.DataCategories.ProductServiceUsage;
        /**
         * To create Seen/Tried/Successful user funnels, events need to contain special DataFields generated from the Funnel contract. These fields are of the form Funnel_Name and Funnel_State.
         * Funnel_Name is an optional name of the feature/operation and Funnel_State defines whether an event is considered Seen/Tried/Successful
         * An event is considered Seen, when the user is exposed to fluid functionality, for example seeing a Fluid component insertion icon in Teams chat
         * An event is considered Tried, when the user tries inserting a Fluid component or loads a component shared with them
         * An event is considered Successful, when the user edits the component, for example inserts an atmention
         * All user actions are considered Successful by default
         * More details https://microsoft.sharepoint-df.com/:x:/t/OXODIG-Team/EbnL-NDqI8lGiAY9bidsVIABiDUE1xlj8VBO5TNNjMmwNg?e=jkhObs
         */
        const userFunnelDataFields = Funnel.getFields('Funnel', {
            name: namespace,
            state: 'Successful'
        });
        const userData = props;
        // Add in the user action contract-specific fields
        const userActionEventDataFields = UserAction.getFields({
            id: userData.actionId,
            name: userActionEventName,
            commandSurface: userData.commandSurface,
            parentName: userData.parentName,
            triggerMethod: userData.triggerMethod,
            timeOffsetMs: userData.timeOffsetMs
        });
        const propsWithoutActivityData = this.prepareBaseActivityOtelEvent(telemetryEvent, props, true);
        const { eventName, actionId, commandSurface, parentName, triggerMethod, timeOffsetMs, ...unusedProps } = propsWithoutActivityData;
        telemetryEvent.dataFields = [
            ...userFunnelDataFields,
            ...userActionEventDataFields,
            // Exception added when eslint rule was added, this should be revisited when modifying this code
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            ...this.getDataFieldsFromProps(unusedProps || {})
        ];
    }
    /**
     * Lazy load the Asha package and initialize Asha.
     * Invoking this for a second or higher time should be a no-op if load was successful.
     * This method should only be called right before attempting to log an Asha event
     */
    loadAsha() {
        if (this.ashaLoadStarted) {
            return;
        }
        this.ashaLoadStarted = true;
        this.ashaUtils = import(/* webpackChunkName: "AshaUtils" */ './AshaUtils');
        this.ashaUtils
            .then(({ initializeAshaForFluid }) => initializeAshaForFluid(this.ariaInfo.namespacePrefix, this.initialPersistentProps, (event) => {
            /**
             * Delete eventContract containing `AshaPartAMetadata`.
             * We will rely on partAMetadata from the telemetry sink.
             * ```json
             * "eventContract": {
             *   "dataFields": [],
             *   "name": "AshaPartAMetadata",
             * }
             * ```
             */
            delete event.eventContract;
            this.telemetryLogger.sendTelemetryEvent(event);
        }))
            .catch(() => {
            this.ashaLoadStarted = false;
            this.logDefaultEventToOtel({
                category: EventCategory.Error,
                props: { eventName: 'AshaLoadFailure' }
            });
        });
    }
}
//# sourceMappingURL=BaseOtelLogProcessor.js.map